import React, { useMemo } from "react";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import DialogContent from "@material-ui/core/DialogContent";
import { Fetcher } from "./Fetcher";
import { grey } from "@material-ui/core/colors";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import { DateDisplay } from "./DateDisplay";
import { ResourceUsageLogResponse } from "../models/SubscriptionNamedResources";
import { getResourceUsage } from "../services/api/subscriptionNamedResources.api";
import { DEFAULT_PAGING_SIZE } from "../constants";
import { List, ListItem, Grid } from "@material-ui/core";
import { ValueDisplay } from "./ValueDisplay";
import { MessageCard } from "./MessageCard";
import { Pagination } from "../components/Pagination";
import { camelToTitleCase } from "../utils";

export interface SubscriptionNamedResourcesUsageDialogProps {
	resourceId: string;
	resourceKey: string;
	detailsOpen: boolean;
	setOpen: (open: boolean) => void;
	subscriptionId: string;
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	appBar: {
		position: "relative",
	},
	tableWrapper: {
		margin: theme.spacing(0.5),
		minHeight: 800,
		maxWidth: 1200,
		overflow: "hidden",
		borderBottomColor: grey[100],
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
	},
	dialogPaper: {
		minHeight: "95vh",
		maxHeight: "100vh",
	},
	title: {
		marginLeft: theme.spacing(2),
		flex: 1,
	},
	dialogBody: {
		margin: theme.spacing(4),
	},
	list: {
		maxWidth: theme.breakpoints.values.md,
	},
	listItem: {
		boxShadow: theme.shadows[1],
		marginBottom: theme.spacing(2),
	},
	details: {
		fontSize: theme.typography.body2.fontSize,
	},
}));

const renderData = (
	data: ResourceUsageLogResponse[],
	classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	pageNumber: number,
	rowsPerPage: number,
) => {
	return (
		<div data-testid="subscription-Resource-usageLogs-list">
			{data && data.length > 0 ? (
				<div>
					<List className={classes.list}>
						{data.map((log) => (
							<ListItem
								key={log.namedResourceId}
								className={classes.listItem}
								data-testid="subscription-Resources-list-item"
							>
								<Grid container justify={"space-between"}>
									<Grid item xs={5}>
										<ValueDisplay
											label={"Usage Log Id"}
											node={<Typography>{camelToTitleCase(log.resourceUsageLogId)}</Typography>}
										></ValueDisplay>
									</Grid>
									<Grid item xs={4}>
										<ValueDisplay
											label={"Result Type"}
											node={<Typography>{camelToTitleCase(log.resultType)}</Typography>}
										></ValueDisplay>
									</Grid>
									<Grid item xs={3}>
										<ValueDisplay
											label={"Timestamp"}
											node={<DateDisplay date={log.timeStamp} fontSize={16}></DateDisplay>}
										/>
									</Grid>
								</Grid>
							</ListItem>
						))}
					</List>
					<Pagination
						pageNumber={pageNumber}
						rowsPerPage={rowsPerPage}
						setPageFunction={setPageNumber}
						setRowsPerPageFunction={setRowsPerPage}
						currentPageLength={data.length}
					></Pagination>
				</div>
			) : (
				<MessageCard message="No resource usage logs found." textAlign="left" />
			)}
		</div>
	);
};

/**
 * Twilio call and chat details dialog
 * @param props
 */
export const SubscriptionNamedResourcesUsageDialog = (props: SubscriptionNamedResourcesUsageDialogProps) => {
	const classes = useStyles();

	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const requestPageNumber = pageNumber + 1;

	return (
		<Dialog
			open={props.detailsOpen}
			onClose={() => {
				props.setOpen(false);
			}}
			classes={{ paper: classes.dialogPaper }}
			fullWidth={true}
			maxWidth={"xl"}
		>
			<AppBar color="default" className={classes.appBar}>
				<Toolbar>
					<IconButton edge="start" color="inherit" onClick={() => props.setOpen(false)} aria-label="close">
						<CloseIcon />
					</IconButton>
					<Typography variant="h6" className={classes.title}>
						{`${props.resourceKey} Usage Logs`}
					</Typography>
				</Toolbar>
			</AppBar>
			<DialogContent>
				{useMemo(() => {
					return (
						<Fetcher
							action={getResourceUsage(props.productId, props.subscriptionId, props.resourceId, {
								pageNumber: requestPageNumber,
								pageSize: rowsPerPage,
							})}
						>
							{(response) => renderData(response.data, classes, setPageNumber, setRowsPerPage, pageNumber, rowsPerPage)}
						</Fetcher>
					);
				}, [
					props.productId,
					props.subscriptionId,
					props.resourceId,
					requestPageNumber,
					rowsPerPage,
					classes,
					pageNumber,
				])}
			</DialogContent>
		</Dialog>
	);
};
